export const ONGOING_CALLS_REFRESH_MS = 5 * 60 * 1000;

export interface FormattedTranscriptLine {
  time: string;
  timeSeconds: number;
  speaker: string;
  role: 'AI' | 'AGENT' | 'CUSTOMER' | 'SYSTEM';
  text: string;
  sender: string;
  content: string;
  formattedTime: string;
}

export interface CrmInsightSection {
  title: string;
  content: string;
}

export interface SummaryLogView {
  managementSummary: string;
  highlights: string[];
  majorIssues: string[];
  callFlow: { title: string; summary: string }[];
  discussionPoints: string[];
  prospectSnapshot: string[];
}

export interface ObjectionHandler {
  objection: string;
  response: string;
}

export interface OngoingCallListItem {
  conversationPid: number;
  traceId: string;
  conversationId?: string;
  customerPid?: number;
  businessName: string;
  phoneNumber: string;
  agentName: string;
  sentiment: string;
  platform: string;
  productName: string;
  startTime: string | null;
}

export interface OngoingCallDetailView {
  businessName: string;
  phoneNumber: string;
  agentName: string;
  sentiment: string;
  platform: string;
  productName: string;
  startTime: string | null;
  transcript: FormattedTranscriptLine[];
  cheatSheet: CrmInsightSection[];
  salesPitch: CrmInsightSection[];
  summaryLogView: SummaryLogView | null;
  objectionHandlers: ObjectionHandler[];
}

export function parseJson(value: unknown): unknown {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

export function toTitleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function applyFormattedTime(line: FormattedTranscriptLine, seconds: number): FormattedTranscriptLine {
  const time = formatTime(seconds);
  return {
    ...line,
    timeSeconds: seconds,
    formattedTime: time,
    time,
  };
}

const MIN_VALID_TRANSCRIPT_YEAR = 2020;
const MAX_VALID_TRANSCRIPT_YEAR = 2035;
const MAX_CALL_OFFSET_SECONDS = 8 * 60 * 60;

function debugTranscriptTime(message: string, data?: unknown): void {
  console.debug(`[LiveTranscriptTime] ${message}`, data ?? '');
}

function pickTranscriptTimeFields(item: Record<string, unknown>): Record<string, unknown> {
  const keys = [
    'timestamp',
    'messageTimestamp',
    'message_timestamp',
    'createdAt',
    'created_at',
    'recordedAt',
    'recorded_at',
    'spokenAt',
    'spoken_at',
    'messageTime',
    'message_time',
    'time',
    'offsetSeconds',
    'offset_seconds',
    'elapsedSeconds',
    'elapsed_seconds',
    'timeInCall',
    'time_in_call',
    'secondsFromStart',
    'seconds_from_start',
    'callOffsetSeconds',
    'call_offset_seconds',
    'timeSeconds',
    'time_seconds',
    'offset',
    'elapsed',
    'duration',
    'callTime',
    'call_time',
    'messageOffset',
    'message_offset',
    'start',
  ];

  const picked: Record<string, unknown> = {};
  for (const key of keys) {
    if (item[key] != null) {
      picked[key] = item[key];
    }
  }
  return picked;
}

function resolveCallStartMs(callStartAt: string | null | undefined): number | null {
  if (!callStartAt?.trim()) return null;
  const parsed = Date.parse(callStartAt);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Seconds elapsed since call start — same anchor used by the live duration timer. */
export function secondsFromCallStart(
  callStartAt: string | null | undefined,
  referenceMs: number = Date.now()
): number | null {
  const callStartMs = resolveCallStartMs(callStartAt);
  if (callStartMs == null) return null;
  return Math.max(0, Math.floor((referenceMs - callStartMs) / 1000));
}

function resolveNumericOffsetSeconds(record: Record<string, unknown>): number | null {
  const keys = [
    'offsetSeconds',
    'offset_seconds',
    'elapsedSeconds',
    'elapsed_seconds',
    'timeInCall',
    'time_in_call',
    'secondsFromStart',
    'seconds_from_start',
    'callOffsetSeconds',
    'call_offset_seconds',
    'timeSeconds',
    'time_seconds',
    'offset',
    'elapsed',
    'duration',
    'callTime',
    'call_time',
    'messageOffset',
    'message_offset',
    'start',
  ];

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && isFinite(value) && value >= 0) {
      return Math.floor(value);
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        return Math.floor(parsed);
      }
    }
  }

  return null;
}

/** Backend malformed format: +{junk}-MM-DDThh:mm:ss.000Z — junk is not elapsed ms; use T time vs callStartAt. */
const VOCAL_TRANS_ENG_MALFORMED_TIMESTAMP =
  /^\+[^-]+-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)$/i;

function parseVocalTransEngMalformedTimestamp(
  raw: string,
  callStartAt: string | null | undefined
): { seconds: number; strategy: string; stitched?: string } | null {
  const trimmed = raw.trim();
  const match = trimmed.match(VOCAL_TRANS_ENG_MALFORMED_TIMESTAMP);
  if (!match || !callStartAt) return null;

  const callStartMs = resolveCallStartMs(callStartAt);
  if (callStartMs == null) return null;

  const timePart = match[1];
  const callStart = new Date(callStartMs);
  const stitched = `${callStart.getUTCFullYear()}-${String(callStart.getUTCMonth() + 1).padStart(2, '0')}-${String(callStart.getUTCDate()).padStart(2, '0')}${timePart}`;
  const stitchedMs = Date.parse(stitched);

  if (!Number.isNaN(stitchedMs)) {
    const offset = Math.floor((stitchedMs - callStartMs) / 1000);
    if (offset >= 0 && offset <= MAX_CALL_OFFSET_SECONDS) {
      return { seconds: offset, strategy: 'call-date-plus-time', stitched };
    }
  }

  const tMatch = timePart.match(/T(\d{2}):(\d{2}):(\d{2})/);
  if (tMatch) {
    const elapsed = Number(tMatch[1]) * 3600 + Number(tMatch[2]) * 60 + Number(tMatch[3]);
    if (elapsed >= 0 && elapsed <= MAX_CALL_OFFSET_SECONDS) {
      return { seconds: elapsed, strategy: 'time-as-elapsed' };
    }
  }

  return null;
}

function resolveTimestampOffsetSeconds(
  item: Record<string, unknown>,
  callStartAt: string | null | undefined,
  debugContext?: { index?: number; textPreview?: string }
): number | null {
  const keys = [
    'timestamp',
    'messageTimestamp',
    'message_timestamp',
    'createdAt',
    'created_at',
    'recordedAt',
    'recorded_at',
    'spokenAt',
    'spoken_at',
    'messageTime',
    'message_time',
    'time',
  ];

  const callStartMs = resolveCallStartMs(callStartAt);
  const attempts: Record<string, unknown>[] = [];

  for (const key of keys) {
    const value = item[key];
    if (value == null) continue;

    if (typeof value === 'number' && isFinite(value)) {
      if (callStartMs != null && value > 1_000_000_000_000) {
        const seconds = Math.max(0, Math.floor((value - callStartMs) / 1000));
        debugTranscriptTime('resolved from absolute epoch field', {
          ...debugContext,
          key,
          value,
          callStartAt,
          seconds,
          formattedTime: formatTime(seconds),
        });
        return seconds;
      }
      if (value >= 0 && value < 10_000_000) {
        const seconds = Math.floor(value);
        debugTranscriptTime('resolved from numeric time field', {
          ...debugContext,
          key,
          value,
          seconds,
          formattedTime: formatTime(seconds),
        });
        return seconds;
      }
      attempts.push({ key, value, reason: 'numeric-out-of-range' });
      continue;
    }

    const raw = String(value).trim();
    if (!raw) continue;

    const malformed = parseVocalTransEngMalformedTimestamp(raw, callStartAt);
    if (malformed != null) {
      debugTranscriptTime('resolved from malformed vocal-trans-eng timestamp', {
        ...debugContext,
        key,
        raw,
        strategy: malformed.strategy,
        stitched: malformed.stitched,
        seconds: malformed.seconds,
        formattedTime: formatTime(malformed.seconds),
        callStartAt,
      });
      return malformed.seconds;
    }

    const parsed = Date.parse(raw);
    const plausible = !Number.isNaN(parsed) && isPlausibleTranscriptTimestampMs(parsed, callStartMs);
    if (plausible) {
      if (callStartMs == null) {
        attempts.push({ key, raw, reason: 'missing-call-start' });
        continue;
      }
      const seconds = Math.max(0, Math.floor((parsed - callStartMs) / 1000));
      debugTranscriptTime('resolved from ISO timestamp', {
        ...debugContext,
        key,
        raw,
        parsed: new Date(parsed).toISOString(),
        callStartAt,
        seconds,
        formattedTime: formatTime(seconds),
      });
      return seconds;
    }

    attempts.push({
      key,
      raw,
      parsed: Number.isNaN(parsed) ? null : new Date(parsed).toISOString(),
      parsedYear: Number.isNaN(parsed) ? null : new Date(parsed).getUTCFullYear(),
      plausible,
      malformed,
      reason: Number.isNaN(parsed) ? 'date-parse-failed' : 'timestamp-rejected',
    });
  }

  debugTranscriptTime('no timestamp offset resolved', {
    ...debugContext,
    callStartAt,
    callStartMs,
    itemKeys: Object.keys(item),
    timeFields: pickTranscriptTimeFields(item),
    attempts,
  });

  return null;
}

function isPlausibleTranscriptTimestampMs(ms: number, callStartMs: number | null): boolean {
  const year = new Date(ms).getUTCFullYear();
  if (year < MIN_VALID_TRANSCRIPT_YEAR || year > MAX_VALID_TRANSCRIPT_YEAR) return false;
  if (callStartMs == null) return true;

  const offsetSec = (ms - callStartMs) / 1000;
  return offsetSec >= -5 && offsetSec <= MAX_CALL_OFFSET_SECONDS;
}

/** Message offset from call start, using the same anchor as the live duration timer. */
function secondsFromCallStartForMessage(
  item: Record<string, unknown>,
  callStartAt: string | null | undefined,
  debugContext?: { index?: number; textPreview?: string }
): number | null {
  const numericOffset = resolveNumericOffsetSeconds(item);
  if (numericOffset != null) {
    debugTranscriptTime('resolved from numeric offset field', {
      ...debugContext,
      callStartAt,
      numericOffset,
      formattedTime: formatTime(numericOffset),
      timeFields: pickTranscriptTimeFields(item),
    });
    return numericOffset;
  }

  return resolveTimestampOffsetSeconds(item, callStartAt, debugContext);
}

function fillMissingTranscriptSeconds(
  lines: FormattedTranscriptLine[],
  missingIndices: number[],
  callStartAt: string | null
): void {
  if (lines.length === 0 || missingIndices.length === 0) return;

  const missing = new Set(missingIndices);
  const knownIndices = lines.map((_, i) => i).filter((i) => !missing.has(i));

  const before = lines.map((line, index) => ({
    index,
    timeSeconds: line.timeSeconds,
    formattedTime: line.formattedTime,
    text: line.text.slice(0, 60),
    missing: missing.has(index),
  }));

  if (knownIndices.length === 0) {
    debugTranscriptTime('fillMissing: no known times, defaulting all to 0:00', {
      callStartAt,
      lineCount: lines.length,
      before,
    });
    lines.forEach((line) => {
      Object.assign(line, applyFormattedTime(line, 0));
    });
    return;
  }

  const firstKnown = knownIndices[0];
  for (let i = 0; i < firstKnown; i++) {
    Object.assign(lines[i], applyFormattedTime(lines[i], lines[firstKnown].timeSeconds));
  }

  let lastKnown = firstKnown;
  for (let i = firstKnown + 1; i < lines.length; i++) {
    if (!missing.has(i)) {
      const startSeconds = lines[lastKnown].timeSeconds;
      const endSeconds = lines[i].timeSeconds;
      const gap = i - lastKnown;
      for (let j = lastKnown + 1; j < i; j++) {
        const seconds = Math.floor(startSeconds + ((endSeconds - startSeconds) * (j - lastKnown)) / gap);
        Object.assign(lines[j], applyFormattedTime(lines[j], seconds));
      }
      lastKnown = i;
    }
  }

  for (let i = lastKnown + 1; i < lines.length; i++) {
    Object.assign(lines[i], applyFormattedTime(lines[i], lines[lastKnown].timeSeconds));
  }

  debugTranscriptTime('fillMissing applied', {
    callStartAt,
    missingIndices,
    knownIndices,
    firstKnown,
    lastKnown,
    before,
    after: lines.map((line, index) => ({
      index,
      timeSeconds: line.timeSeconds,
      formattedTime: line.formattedTime,
      text: line.text.slice(0, 60),
    })),
  });
}

function maxTranscriptSeconds(segments: Map<string, FormattedTranscriptLine>): number {
  let max = 0;
  for (const line of segments.values()) {
    if (line.timeSeconds > max) max = line.timeSeconds;
  }
  return max;
}

/**
 * Detects when transcript timestamps are absolute positions within a continuous audio
 * stream (not relative to the call start) and normalises them in-place.
 *
 * When the highest timestamp in the transcript exceeds the current call elapsed time
 * by more than a 30-second buffer the timestamps cannot be call-relative — they must
 * be stream-absolute offsets (e.g. a Deepgram stream that runs across multiple calls).
 * Subtracting the minimum offset across all lines converts them to call-relative times.
 */
function normalizeStreamAbsoluteTimestamps(
  lines: FormattedTranscriptLine[],
  callStartAt: string | null | undefined
): void {
  if (lines.length === 0) return;

  const elapsed = secondsFromCallStart(callStartAt);
  if (elapsed == null) return;

  const maxTime = lines.reduce((m, l) => Math.max(m, l.timeSeconds), 0);
  if (maxTime <= elapsed + 30) return; // already within call duration

  const minTime = lines.reduce((m, l) => Math.min(m, l.timeSeconds), Infinity);
  if (!isFinite(minTime) || minTime === 0) return;

  debugTranscriptTime('normalizeStreamAbsoluteTimestamps: shifting timestamps', {
    callStartAt,
    elapsed,
    maxTime,
    minTime,
    lineCount: lines.length,
  });

  lines.forEach((l) => {
    Object.assign(l, applyFormattedTime(l, Math.max(0, l.timeSeconds - minTime)));
  });
}

export function formatDurationFromStart(startTime: string | null): string {
  const elapsed = secondsFromCallStart(startTime);
  if (elapsed == null) return '00:00';
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function extractApiArray(res: unknown): unknown[] {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  const obj = res as Record<string, unknown>;
  if (Array.isArray(obj['data'])) return obj['data'] as unknown[];
  if (Array.isArray(obj['results'])) return obj['results'] as unknown[];
  if (Array.isArray(obj['items'])) return obj['items'] as unknown[];
  if (obj['data'] && typeof obj['data'] === 'object') {
    const nested = obj['data'] as Record<string, unknown>;
    if (Array.isArray(nested['data'])) return nested['data'] as unknown[];
    if (Array.isArray(nested['results'])) return nested['results'] as unknown[];
  }
  return [];
}

export function extractApiObject(res: unknown): Record<string, unknown> | null {
  if (!res || typeof res !== 'object') return null;
  const obj = res as Record<string, unknown>;
  if (obj['data'] && typeof obj['data'] === 'object' && !Array.isArray(obj['data'])) {
    return obj['data'] as Record<string, unknown>;
  }
  return obj;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        return Object.values(item)
          .filter((part) => typeof part === 'string' && part.trim())
          .join(' — ')
          .trim();
      }
      return '';
    })
    .filter((item) => item.length > 0);
}

function formatInlineValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function formatInsightValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  const isExcludedKey = (k: string) => {
    const lower = k.toLowerCase();
    return lower === 'evidence_source_ids' || lower === 'evidencesourceids' || lower === 'evidence_source_id' || lower === 'evidencesourceid';
  };

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'object' && item !== null
          ? Object.entries(item)
              .filter(([key]) => !isExcludedKey(key))
              .map(([key, val]) => `- **${toTitleCase(key.replace(/_/g, ' '))}:** ${formatInlineValue(val)}`)
              .join('\n')
          : `- ${formatInlineValue(item)}`
      )
      .join('\n');
  }
  return Object.entries(value as Record<string, unknown>)
    .filter(([key]) => !isExcludedKey(key))
    .map(([key, val]) => `- **${toTitleCase(key.replace(/_/g, ' '))}:** ${formatInlineValue(val)}`)
    .join('\n');
}

export function toInsightSections(value: unknown): CrmInsightSection[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    return [{ title: 'Details', content: value }];
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => {
      const record = item as Record<string, unknown>;
      const title =
        typeof item === 'object' && item !== null && record['question'] != null
          ? String(record['question'] || `Item ${index + 1}`)
          : typeof item === 'object' && item !== null && record['title'] != null
            ? String(record['title'] || `Item ${index + 1}`)
            : `Item ${index + 1}`;
      const content =
        typeof item === 'object' && item !== null
          ? String(record['answer'] ?? record['response'] ?? formatInsightValue(item))
          : formatInsightValue(item);
      return { title, content };
    });
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).map(([title, content]) => ({
      title: toTitleCase(title.replace(/_/g, ' ')),
      content: formatInsightValue(content),
    }));
  }
  return [{ title: 'Details', content: String(value) }];
}

export function buildLeadInsightSections(entries: unknown, key: string): CrmInsightSection[] {
  if (!Array.isArray(entries)) {
    const direct = (entries as Record<string, unknown>)?.[key];
    if (direct != null) return toInsightSections(parseJson(direct) ?? direct);
    return [];
  }

  return entries
    .map((entry, index) => {
      const record = entry as Record<string, unknown>;
      const value = record?.[key];
      if (value == null || value === '') return [];
      const parsed = parseJson(value) ?? value;
      const sections = toInsightSections(parsed);
      const suffix = entries.length > 1 ? ` ${index + 1}` : '';
      return sections.length
        ? sections.map((section) => ({
            ...section,
            title: `${section.title}${suffix}`,
          }))
        : [
            {
              title: `${toTitleCase(key.replace(/_/g, ' '))}${suffix}`,
              content: String(value),
            },
          ];
    })
    .flat()
    .filter((section) => section.content.trim().length > 0);
}

export function buildSummaryLogView(summaryLog: unknown): SummaryLogView | null {
  if (!summaryLog || typeof summaryLog !== 'object') return null;
  const log = summaryLog as Record<string, unknown>;
  if (log['errorPayload']) return null;

  const summaryRaw = log['summary'];
  const summary =
    typeof summaryRaw === 'string' ? (parseJson(summaryRaw) as Record<string, unknown>) : summaryRaw;
  const summaryObj = (summary && typeof summary === 'object' ? summary : {}) as Record<string, unknown>;

  const managementSummary = String(log['managementSummary'] || '').trim();
  const highlights = toStringArray(summaryObj['interest_signals']);
  const majorIssues = [
    ...toStringArray(summaryObj['open_queries']),
    ...(Array.isArray(summaryObj['objection_tracker'])
      ? summaryObj['objection_tracker']
          .map((item: unknown) =>
            item && typeof item === 'object' ? (item as Record<string, unknown>)['concern'] : null
          )
          .filter((concern): concern is string => typeof concern === 'string' && !!concern.trim())
      : []),
  ];
  const callFlow = Array.isArray(summaryObj['call_flow'])
    ? summaryObj['call_flow']
        .map((item: unknown) => {
          const row = item as Record<string, unknown>;
          return {
            title: String(row?.['title'] || '').trim(),
            summary: String(row?.['summary'] || '').trim(),
          };
        })
        .filter((item) => item.title || item.summary)
    : [];
  const discussionPoints = toStringArray(summaryObj['discussion_points']);
  const prospectSnapshot = toStringArray(summaryObj['prospect_snapshot']);

  const hasContent =
    managementSummary.length > 0 ||
    highlights.length > 0 ||
    majorIssues.length > 0 ||
    callFlow.length > 0 ||
    discussionPoints.length > 0 ||
    prospectSnapshot.length > 0;

  if (!hasContent) return null;

  return {
    managementSummary,
    highlights,
    majorIssues,
    callFlow,
    discussionPoints,
    prospectSnapshot,
  };
}

export function buildObjectionHandlers(source: unknown): ObjectionHandler[] {
  const summary = source && typeof source === 'object' ? (source as Record<string, unknown>) : null;
  const trackers = [
    ...(Array.isArray(summary?.['objection_tracker']) ? summary!['objection_tracker'] : []),
    ...(Array.isArray(summary?.['objection_handlers']) ? summary!['objection_handlers'] : []),
    ...(Array.isArray(summary?.['objectionHandlers']) ? summary!['objectionHandlers'] : []),
  ] as Record<string, unknown>[];

  return trackers
    .map((item) => ({
      objection: String(
        item?.['objection'] ?? item?.['concern'] ?? item?.['title'] ?? item?.['label'] ?? ''
      ).trim(),
      response: String(
        item?.['response'] ?? item?.['handler'] ?? item?.['suggestion'] ?? item?.['answer'] ?? ''
      ).trim(),
    }))
    .filter((item) => item.objection || item.response);
}

export function pickString(source: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const val = source[key];
    if (val != null && String(val).trim()) return String(val).trim();
  }
  return '';
}

function pickNestedString(source: Record<string, unknown>, path: string[]): string {
  let current: unknown = source;
  for (const key of path) {
    if (!current || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[key];
  }
  return current != null ? String(current).trim() : '';
}

function buildOngoingCheatSheetSections(cheatSheet: Record<string, unknown>): CrmInsightSection[] {
  const sections: CrmInsightSection[] = [];

  const openingHook = cheatSheet['opening_hook'];
  if (openingHook) {
    sections.push({ title: 'Opening Hook', content: String(openingHook) });
  }

  const valueProps = cheatSheet['value_props'];
  if (Array.isArray(valueProps) && valueProps.length) {
    sections.push({
      title: 'Value Props',
      content: valueProps.map((item) => `- ${String(item)}`).join('\n'),
    });
  }

  const callPrep = cheatSheet['call_prep'];
  if (callPrep && typeof callPrep === 'object') {
    sections.push({ title: 'Call Prep', content: formatInsightValue(callPrep) });
  }

  const businessSnapshot = cheatSheet['business_snapshot'];
  if (businessSnapshot) {
    sections.push({ title: 'Business Snapshot', content: String(businessSnapshot) });
  }

  const strategicConclusion = cheatSheet['strategic_conclusion'];
  if (strategicConclusion) {
    sections.push({ title: 'Strategic Conclusion', content: String(strategicConclusion) });
  }

  const painPoints = cheatSheet['strategic_pain_points'];
  if (Array.isArray(painPoints) && painPoints.length) {
    sections.push({ title: 'Strategic Pain Points', content: formatInsightValue(painPoints) });
  }

  const opportunities = cheatSheet['prioritized_opportunities'];
  if (Array.isArray(opportunities) && opportunities.length) {
    sections.push({ title: 'Prioritized Opportunities', content: formatInsightValue(opportunities) });
  }

  const dataGaps = cheatSheet['data_gaps'];
  if (Array.isArray(dataGaps) && dataGaps.length) {
    const gaps = dataGaps
      .filter((gap) => {
        const row = gap as Record<string, unknown>;
        return row['action'] !== 'skip';
      })
      .map((gap) => {
        const row = gap as Record<string, unknown>;
        const label = String(row['gap'] || 'Gap').trim();
        const instruction = String(row['instruction'] || '').trim();
        return instruction ? `**${label}:** ${instruction}` : `**${label}**`;
      })
      .join('\n\n');
    if (gaps) {
      sections.push({ title: 'Data Gaps', content: gaps });
    }
  }

  return sections.filter((section) => section.content.trim().length > 0);
}

function buildOngoingSalesPitchSections(pitch: Record<string, unknown>): CrmInsightSection[] {
  const sections: CrmInsightSection[] = [];

  if (pitch['opener']) {
    sections.push({ title: 'Opener', content: String(pitch['opener']) });
  }
  if (pitch['relevance_hook']) {
    sections.push({ title: 'Relevance Hook', content: String(pitch['relevance_hook']) });
  }

  const talkingPoints = pitch['key_talking_points'];
  if (Array.isArray(talkingPoints) && talkingPoints.length) {
    sections.push({
      title: 'Key Talking Points',
      content: talkingPoints.map((item) => `- ${String(item)}`).join('\n'),
    });
  }

  const discoveryQuestions = pitch['discovery_questions'];
  if (Array.isArray(discoveryQuestions) && discoveryQuestions.length) {
    sections.push({
      title: 'Discovery Questions',
      content: discoveryQuestions.map((item) => `- ${String(item)}`).join('\n'),
    });
  }

  const proofPoints = pitch['proof_points'];
  if (Array.isArray(proofPoints) && proofPoints.length) {
    sections.push({ title: 'Proof Points', content: formatInsightValue(proofPoints) });
  }

  if (pitch['demo_booking_cta']) {
    sections.push({ title: 'Demo Booking CTA', content: String(pitch['demo_booking_cta']) });
  }
  if (pitch['urgency_rationale']) {
    sections.push({ title: 'Urgency Rationale', content: String(pitch['urgency_rationale']) });
  }
  if (pitch['emotional_frame']) {
    sections.push({ title: 'Emotional Frame', content: String(pitch['emotional_frame']) });
  }

  return sections.filter((section) => section.content.trim().length > 0);
}

function buildOngoingPitchObjectionHandlers(source: unknown): ObjectionHandler[] {
  if (!source || typeof source !== 'object') return [];
  const handlers = (source as Record<string, unknown>)['objection_handlers'];
  if (!Array.isArray(handlers)) return [];

  return handlers
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        objection: String(row['objection'] ?? '').trim(),
        response: String(row['response'] ?? '').trim(),
      };
    })
    .filter((item) => item.objection || item.response);
}

function mapOngoingCallCommonFields(item: Record<string, unknown>) {
  const customerPid = Number(item['customerPid'] ?? item['customer_pid']) || undefined;

  return {
    businessName:
      pickString(item, [
        'customerBusinessName',
        'businessName',
        'leadName',
        'customerName',
        'companyName',
        'business_name',
      ]) || (customerPid ? `Lead #${customerPid}` : 'Unknown'),
    phoneNumber: pickString(item, [
      'customerPhoneNumber',
      'phoneNumber',
      'phone',
      'customerPhone',
      'phone_number',
    ]),
    agentName:
      pickString(item, ['agentName', 'agent_name']) ||
      pickNestedString(item, ['agentDetails', 'name']) ||
      'Unknown Agent',
    sentiment:
      pickString(item, ['sentiment', 'callSentiment']) ||
      pickNestedString(item, ['bizIntel', 'sentiment']) ||
      'Neutral',
    platform: pickString(item, ['platformType', 'platform', 'platform_type']),
    productName: pickString(item, ['productName', 'productKeyname', 'product_name']),
    startTime:
      pickString(item, [
        'callStartAt',
        'conversationStartTime',
        'callStartTime',
        'startedAt',
        'conversation_start_time',
      ]) || null,
    customerPid,
  };
}

export function mapOngoingCallListItem(raw: unknown): OngoingCallListItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const conversationPid = Number(item['conversationPid'] ?? item['conversation_pid']);
  const traceIdRaw = item['traceId'] ?? item['trace_id'];
  const traceId = traceIdRaw != null ? String(traceIdRaw).trim() : '';
  if (!conversationPid || !traceId) return null;

  const common = mapOngoingCallCommonFields(item);

  return {
    conversationPid,
    traceId,
    conversationId: pickString(item, ['conversationId', 'conversation_id']) || undefined,
    customerPid: common.customerPid,
    businessName: common.businessName,
    phoneNumber: common.phoneNumber,
    agentName: common.agentName,
    sentiment: common.sentiment,
    platform: common.platform,
    productName: common.productName,
    startTime: common.startTime,
  };
}

function resolveTranscriptArray(value: unknown): unknown[] | null {
  const parsed = parseJson(value) ?? value;
  return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
}

function extractTranscriptText(record: Record<string, unknown>): string {
  return pickString(record, [
    'message',
    'text',
    'content',
    'transcript',
    'utterance',
    'transcriptText',
  ]);
}

function resolveTranscriptSpeaker(
  record: Record<string, unknown>,
  agentName: string,
  businessName: string
): { sender: string; role: FormattedTranscriptLine['role'] } {
  const speakerName = pickString(record, ['speaker', 'speakerName', 'sender', 'clientName']);
  const roleRaw = String(record['role'] ?? record['speakerRole'] ?? '').toLowerCase();
  const speakerNum = record['speaker'];
  const speakerLower = speakerName.toLowerCase();
  const agentLower = agentName.toLowerCase();

  const isAgent =
    record['isAgent'] === true ||
    speakerNum === 0 ||
    ['agent', 'advisor', 'salesagent'].includes(roleRaw) ||
    (agentLower && speakerLower === agentLower) ||
    (agentLower && speakerLower.includes(agentLower)) ||
    speakerLower.includes('sales agent');
  const isAI =
    ['ai', 'llm', 'assistant'].includes(roleRaw) || speakerLower.includes('assistant');

  if (isAI) {
    return { sender: speakerName || 'AI', role: 'AI' };
  }
  if (isAgent) {
    return { sender: speakerName || agentName || 'Agent', role: 'AGENT' };
  }
  return {
    sender: speakerName || businessName || 'Customer',
    role: 'CUSTOMER',
  };
}

function mapTranscriptMessageArray(
  messages: unknown[],
  agentName: string,
  businessName: string,
  callStartAt: string | null = null
): FormattedTranscriptLine[] {
  const missingIndices: number[] = [];

  debugTranscriptTime('mapTranscriptMessageArray start', {
    callStartAt,
    callStartMs: resolveCallStartMs(callStartAt),
    messageCount: messages.length,
    sampleRawMessage:
      messages[0] && typeof messages[0] === 'object'
        ? pickTranscriptTimeFields(messages[0] as Record<string, unknown>)
        : messages[0],
    sampleMessageKeys:
      messages[0] && typeof messages[0] === 'object'
        ? Object.keys(messages[0] as Record<string, unknown>)
        : [],
  });

  const lines = messages
    .filter((msg: unknown) => {
      if (typeof msg === 'string') return msg.trim().length > 0;
      return !!extractTranscriptText(msg as Record<string, unknown>);
    })
    .map((msg: unknown, index: number) => {
      if (typeof msg === 'string' && msg.trim()) {
        missingIndices.push(index);
        return {
          timeSeconds: index,
          formattedTime: formatTime(index),
          time: formatTime(index),
          sender: businessName || 'Customer',
          speaker: businessName || 'Customer',
          role: 'CUSTOMER' as FormattedTranscriptLine['role'],
          content: msg,
          text: msg,
        };
      }

      const m = msg as Record<string, unknown>;
      const text = extractTranscriptText(m);
      const { sender, role } = resolveTranscriptSpeaker(m, agentName, businessName);

      let seconds = secondsFromCallStartForMessage(m, callStartAt, {
        index,
        textPreview: text.slice(0, 60),
      });

      if (seconds == null) {
        missingIndices.push(index);
        seconds = 0;
        debugTranscriptTime('message marked missing time', {
          index,
          callStartAt,
          textPreview: text.slice(0, 60),
          timeFields: pickTranscriptTimeFields(m),
        });
      }

      return {
        timeSeconds: seconds,
        formattedTime: formatTime(seconds),
        time: formatTime(seconds),
        sender,
        speaker: sender,
        role,
        content: text,
        text,
      };
    });

  fillMissingTranscriptSeconds(lines, missingIndices, callStartAt);
  normalizeStreamAbsoluteTimestamps(lines, callStartAt);

  debugTranscriptTime('mapTranscriptMessageArray done', {
    callStartAt,
    missingCount: missingIndices.length,
    uniqueTimes: [...new Set(lines.map((line) => line.formattedTime))],
    lines: lines.map((line, index) => ({
      index,
      formattedTime: line.formattedTime,
      timeSeconds: line.timeSeconds,
      sender: line.sender,
      text: line.text.slice(0, 60),
    })),
  });

  return lines;
}

function mapDeepgramTranscript(
  deepgram: unknown[],
  agentName: string,
  callStartAt: string | null = null
): FormattedTranscriptLine[] {
  const lines = deepgram.map((utterance: unknown) => {
    const u = utterance as Record<string, unknown>;
    const seconds = Math.floor(Number(u['start'] ?? 0));
    const speakerNum = u['speaker'] ?? 0;
    const isAgent = speakerNum === 0;
    return {
      timeSeconds: seconds,
      formattedTime: formatTime(seconds),
      time: formatTime(seconds),
      sender: isAgent ? agentName || 'Agent' : 'Customer',
      speaker: isAgent ? agentName || 'Agent' : 'Customer',
      role: isAgent ? ('AGENT' as FormattedTranscriptLine['role']) : ('CUSTOMER' as FormattedTranscriptLine['role']),
      content: String(u['transcript'] || ''),
      text: String(u['transcript'] || ''),
    };
  });
  normalizeStreamAbsoluteTimestamps(lines, callStartAt);
  return lines;
}

export function normalizeOngoingTranscript(
  detail: Record<string, unknown>,
  agentName: string,
  businessName: string,
  callStartAt: string | null = null
): FormattedTranscriptLine[] {
  const conv = detail['customerConversationEntries'] as unknown;
  const conversation = Array.isArray(conv) ? (conv[0] as Record<string, unknown>) : detail;
  const startTime =
    callStartAt ??
    (pickString(detail, [
      'callStartAt',
      'conversationStartTime',
      'callStartTime',
      'startedAt',
      'conversation_start_time',
    ]) || null);

  const apiTranscript =
    resolveTranscriptArray(detail['transcript']) ??
    resolveTranscriptArray(detail['full_transcription']) ??
    resolveTranscriptArray(detail['fullTranscription']) ??
    resolveTranscriptArray(conversation?.['fullTranscription']);

  if (apiTranscript) {
    return mapTranscriptMessageArray(apiTranscript, agentName, businessName, startTime);
  }

  const auditReports = conversation?.['callAuditReport'] as unknown;
  const auditEntry = Array.isArray(auditReports) ? (auditReports[0] as Record<string, unknown>) : null;
  const deepgram = parseJson(auditEntry?.['conversation']);

  if (Array.isArray(deepgram) && deepgram.length > 0 && (deepgram[0] as Record<string, unknown>)['transcript']) {
    return mapDeepgramTranscript(deepgram, agentName, startTime);
  }

  return [];
}

export function mapOngoingCallDetail(raw: unknown): OngoingCallDetailView | null {
  const detail = extractApiObject(raw) ?? (raw as Record<string, unknown>);
  if (!detail || typeof detail !== 'object') return null;

  const common = mapOngoingCallCommonFields(detail);
  const salePitchRoot = detail['salePitch'] as Record<string, unknown> | undefined;
  const cheatSheetRaw =
    salePitchRoot?.['cheat_sheet'] ?? detail['cheat_sheet'] ?? detail['cheatSheet'];
  const l1PitchRaw =
    salePitchRoot?.['l1_sales_pitch'] ?? detail['l1_sales_pitch'] ?? detail['salesPitch'];

  const legacySalePitchEntries =
    detail['customerSalePitchLogsEntries'] ??
    detail['salePitchLogsEntries'] ??
    detail['customer_sale_pitch_logs_entries'];

  let cheatSheet: CrmInsightSection[] = [];
  if (cheatSheetRaw && typeof cheatSheetRaw === 'object') {
    cheatSheet = buildOngoingCheatSheetSections(cheatSheetRaw as Record<string, unknown>);
  } else {
    const cheatSheetDirect = detail['cheat_sheet'] ?? detail['cheatSheet'];
    cheatSheet = [
      ...buildLeadInsightSections(legacySalePitchEntries, 'cheat_sheet'),
      ...(cheatSheetDirect != null
        ? toInsightSections(parseJson(cheatSheetDirect) ?? cheatSheetDirect)
        : []),
    ];
  }

  let salesPitch: CrmInsightSection[] = [];
  if (l1PitchRaw && typeof l1PitchRaw === 'object') {
    salesPitch = buildOngoingSalesPitchSections(l1PitchRaw as Record<string, unknown>);
  } else {
    const salesPitchDirect = detail['l1_sales_pitch'] ?? detail['salesPitch'];
    salesPitch = [
      ...buildLeadInsightSections(legacySalePitchEntries, 'l1_sales_pitch'),
      ...(salesPitchDirect != null
        ? toInsightSections(parseJson(salesPitchDirect) ?? salesPitchDirect)
        : []),
    ];
  }

  const summaryLog =
    detail['summaryLog'] ?? detail['summaryLogsEntries'] ?? detail['summary_logs_entries'];
  const summaryLogEntry = Array.isArray(summaryLog) ? summaryLog[0] : summaryLog;
  const summaryLogView = buildSummaryLogView(summaryLogEntry);

  const summaryParsed =
    summaryLogEntry && typeof summaryLogEntry === 'object'
      ? parseJson((summaryLogEntry as Record<string, unknown>)['summary']) ??
        (summaryLogEntry as Record<string, unknown>)['summary']
      : null;

  let objectionHandlers = buildOngoingPitchObjectionHandlers(l1PitchRaw);
  if (objectionHandlers.length === 0) {
    objectionHandlers = buildObjectionHandlers(summaryParsed ?? detail);
  }

  return {
    businessName: common.businessName,
    phoneNumber: common.phoneNumber,
    agentName: common.agentName,
    sentiment: common.sentiment,
    platform: common.platform,
    productName: common.productName,
    startTime: common.startTime,
    transcript: normalizeOngoingTranscript(
      detail,
      common.agentName,
      common.businessName,
      common.startTime
    ),
    cheatSheet,
    salesPitch,
    summaryLogView,
    objectionHandlers,
  };
}

export function getSentimentClasses(sentiment: string): string {
  const normalized = sentiment.toLowerCase();
  if (normalized.includes('positive')) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/40';
  }
  if (normalized.includes('negative')) {
    return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40';
  }
  return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/40';
}

export function unwrapSocketPayload(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  const root = data as Record<string, unknown>;
  const nested = root['payload'] ?? root['data'] ?? root['output'] ?? root;
  return nested && typeof nested === 'object' && !Array.isArray(nested)
    ? (nested as Record<string, unknown>)
    : root;
}

export function extractSocketCallIds(data: unknown): {
  conversationPid?: number;
  traceId?: string;
  conversationId?: string;
} {
  const root = unwrapSocketPayload(data);
  if (!root) return {};

  const conversationPid = Number(
    root['conversationPid'] ?? root['conversation_pid'] ?? root['id']
  );
  const traceIdRaw = root['traceId'] ?? root['trace_id'];
  const traceId = traceIdRaw != null ? String(traceIdRaw).trim() : undefined;

  return {
    conversationPid: conversationPid || undefined,
    traceId: traceId || undefined,
    conversationId: pickString(root, ['conversationId', 'conversation_id']) || undefined,
  };
}

export function dedupeOngoingCalls(calls: OngoingCallListItem[]): OngoingCallListItem[] {
  const byKey = new Map<string, OngoingCallListItem>();

  for (const call of calls) {
    const dedupeKey = call.customerPid
      ? `customer:${call.customerPid}`
      : `${call.phoneNumber}|${call.agentName}|${call.businessName}`.toLowerCase();

    const existing = byKey.get(dedupeKey);
    if (!existing || call.conversationPid > existing.conversationPid) {
      byKey.set(dedupeKey, call);
    }
  }

  return Array.from(byKey.values()).sort(
    (a, b) => (b.conversationPid ?? 0) - (a.conversationPid ?? 0)
  );
}

export function matchesOngoingCall(data: unknown, call: OngoingCallListItem): boolean {
  const ids = extractSocketCallIds(data);

  if (ids.conversationPid && ids.traceId) {
    return ids.conversationPid === call.conversationPid && ids.traceId === call.traceId;
  }
  if (call.conversationId && ids.conversationId) {
    return call.conversationId === ids.conversationId;
  }
  if (ids.conversationPid) {
    return ids.conversationPid === call.conversationPid;
  }
  return false;
}

function resolveTranscriptSeconds(root: Record<string, unknown>): number {
  const numeric = Math.floor(
    Number(
      root['start'] ??
        root['timeSeconds'] ??
        root['offsetSeconds'] ??
        root['startTime'] ??
        root['elapsedSeconds'] ??
        0
    )
  );
  if (numeric > 0) return numeric;
  return 0;
}

function unwrapTranscriptItem(item: unknown): Record<string, unknown> | null {
  if (!item || typeof item !== 'object') return null;
  const record = item as Record<string, unknown>;
  const nested = record['transcript'] ?? record['utterance'];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return record;
}

function applyRelativeTranscriptTime(
  line: FormattedTranscriptLine,
  item: unknown,
  callStartAt: string | null | undefined
): FormattedTranscriptLine {
  const record = unwrapTranscriptItem(item);
  if (!record) return line;

  const offsetSeconds = secondsFromCallStartForMessage(record, callStartAt);
  if (offsetSeconds == null) return line;

  return applyFormattedTime(line, offsetSeconds);
}

function utteranceFingerprint(item: unknown): string | null {
  const record = unwrapTranscriptItem(item);
  if (!record) return null;
  const text = pickString(record, [
    'transcript',
    'text',
    'content',
    'message',
    'utterance',
    'transcriptText',
  ]);
  if (!text) return null;

  const timestamp = record['timestamp'] != null ? String(record['timestamp']) : '';
  const speaker = pickString(record, ['speaker', 'speakerName', 'role', 'sender']) ?? '';
  return `${timestamp}|${speaker}|${text}`;
}



function isReceivedInExistingSegments(
  received: unknown,
  segments: Map<string, FormattedTranscriptLine>
): boolean {
  const receivedFingerprint = utteranceFingerprint(received);
  if (receivedFingerprint) {
    for (const line of segments.values()) {
      const lineFingerprint = `${line.timeSeconds}|${line.sender}|${line.text}`;
      if (lineFingerprint === receivedFingerprint) return true;
    }
    return false;
  }

  if (typeof received === 'string' && received.trim()) {
    return [...segments.values()].some((line) => line.text === received.trim());
  }

  return false;
}

function mapTranscriptUtterance(
  root: Record<string, unknown>,
  agentName: string,
  businessName: string,
  fallbackKey: string
): { line: FormattedTranscriptLine; segmentKey: string } | null {
  const text = extractTranscriptText(root);
  if (!text) return null;

  const seconds = resolveTranscriptSeconds(root);
  const { sender, role } = resolveTranscriptSpeaker(root, agentName, businessName);

  const segmentKey = String(
    root['segmentId'] ??
      root['utteranceId'] ??
      root['transcriptId'] ??
      root['id'] ??
      root['aiSuggestionsId'] ??
      fallbackKey
  );

  return {
    segmentKey,
    line: {
      timeSeconds: seconds,
      formattedTime: formatTime(seconds),
      time: formatTime(seconds),
      sender,
      speaker: sender,
      role,
      content: text,
      text,
    },
  };
}

export function mapSocketTranscriptLine(
  data: unknown,
  agentName: string,
  businessName: string
): { line: FormattedTranscriptLine; segmentKey: string } | null {
  const root = unwrapSocketPayload(data);
  if (!root) return null;

  const record = unwrapTranscriptItem(root);
  if (!record) return null;

  return mapTranscriptUtterance(
    record,
    agentName,
    businessName,
    `${Math.floor(Number(record['start'] ?? 0))}-${extractTranscriptText(record).slice(0, 32)}`
  );
}

export function mapSocketFullTranscript(
  data: unknown,
  agentName: string,
  businessName: string
): Map<string, FormattedTranscriptLine> | null {
  const segments = new Map<string, FormattedTranscriptLine>();
  if (!applySocketTranscriptUpdate(segments, data, agentName, businessName)) {
    return null;
  }
  return segments;
}

function mapTranscriptItem(
  item: unknown,
  agentName: string,
  businessName: string,
  segmentKey: string
): FormattedTranscriptLine | null {
  if (typeof item === 'string' && item.trim()) {
    const seconds = Number(segmentKey.replace(/^full-/, '')) || 0;
    return {
      timeSeconds: seconds,
      formattedTime: formatTime(seconds),
      time: formatTime(seconds),
      sender: businessName || 'Customer',
      speaker: businessName || 'Customer',
      role: 'CUSTOMER',
      content: item,
      text: item,
    };
  }

  if (!item || typeof item !== 'object') return null;

  const record = unwrapTranscriptItem(item);
  if (!record) return null;

  const mapped = mapTranscriptUtterance(
    record,
    agentName,
    businessName,
    segmentKey
  );
  return mapped?.line ?? null;
}

function buildReceivedSegmentKey(
  item: Record<string, unknown>,
  line: FormattedTranscriptLine
): string {
  const fingerprint = utteranceFingerprint(item);
  if (fingerprint) return `received-${fingerprint.slice(0, 80)}`;
  return `received-${line.timeSeconds}-${line.text.slice(0, 48)}`;
}

/** Appends latestReceivedTranscript (new utterance). Prior chat comes from the API detail response. */
export function applySocketTranscriptUpdate(
  segments: Map<string, FormattedTranscriptLine>,
  data: unknown,
  agentName: string,
  businessName: string,
  callStartAt: string | null = null
): boolean {
  const root = unwrapSocketPayload(data);
  if (!root) return false;

  const latestReceived =
    root['LatestReceivedTranscript'] ??
    root['latestReceivedTranscript'] ??
    root['latest_received_transcript'];
  if (latestReceived == null) {
    console.debug('[LiveTranscript] applySocketTranscriptUpdate: no latestReceivedTranscript', {
      rootKeys: Object.keys(root),
    });
    return false;
  }

  if (isReceivedInExistingSegments(latestReceived, segments)) {
    console.debug('[LiveTranscript] latestReceivedTranscript skipped (already present)', {
      fingerprint: utteranceFingerprint(latestReceived),
    });
    return true;
  }



  const resolvedCallStartAt =
    callStartAt ??
    (pickString(root, ['callStartAt', 'conversationStartTime', 'callStartTime', 'startedAt']) ||
      null);

  let receivedLine: FormattedTranscriptLine | null = null;
  let receivedKey = '';

  if (typeof latestReceived === 'string' && latestReceived.trim()) {
    const line = mapTranscriptItem(latestReceived, agentName, businessName, 'received');
    if (line) {
      receivedLine = line;
      receivedKey = `received-${segments.size}-${line.text.slice(0, 48)}`;
    }
  } else if (typeof latestReceived === 'object') {
    const record = unwrapTranscriptItem(latestReceived);
    if (record) {
      const mapped = mapTranscriptUtterance(record, agentName, businessName, 'received');
      if (mapped) {
        receivedLine = applyRelativeTranscriptTime(mapped.line, record, resolvedCallStartAt);
        receivedKey = buildReceivedSegmentKey(record, receivedLine);
      }
    }
  }

  if (!receivedLine) {
    console.debug('[LiveTranscript] latestReceivedTranscript not mapped', {
      fingerprint: utteranceFingerprint(latestReceived),
      payload: latestReceived,
    });
    return false;
  }

  const hasCallRelativeOffset =
    typeof latestReceived === 'object' &&
    secondsFromCallStartForMessage(
      latestReceived as Record<string, unknown>,
      resolvedCallStartAt
    ) != null;

  if (resolvedCallStartAt) {
    const elapsed = secondsFromCallStart(resolvedCallStartAt);
    if (elapsed != null) {
      // If the timestamp has no call-relative anchor OR it exceeds the actual call
      // duration (stream-absolute position), fall back to the current elapsed time.
      const exceedsCallDuration = receivedLine.timeSeconds > elapsed + 30;
      if (!hasCallRelativeOffset || exceedsCallDuration) {
        const clampedSeconds = Math.max(maxTranscriptSeconds(segments), elapsed);
        debugTranscriptTime('socket message inferred from call elapsed', {
          resolvedCallStartAt,
          elapsed,
          reason: !hasCallRelativeOffset ? 'no-call-relative-offset' : 'exceeds-call-duration',
          originalTimeSeconds: receivedLine.timeSeconds,
          clampedSeconds,
          formattedTime: formatTime(clampedSeconds),
          textPreview: receivedLine.text.slice(0, 60),
        });
        receivedLine = applyFormattedTime(receivedLine, clampedSeconds);
      }
    }
  }

  segments.set(receivedKey, receivedLine);
  console.debug('[LiveTranscript] latestReceivedTranscript appended', {
    fingerprint: utteranceFingerprint(latestReceived),
    receivedKey,
    textPreview: receivedLine.text.slice(0, 80),
  });
  return true;
}

export function mapSocketSummaryUpdate(data: unknown): SummaryLogView | null {
  const root = unwrapSocketPayload(data);
  if (!root) return null;

  const summaryLog =
    root['summaryLog'] ??
    (root['summary'] || root['managementSummary']
      ? {
          summary: root['summary'],
          managementSummary: root['managementSummary'],
        }
      : null);

  return buildSummaryLogView(summaryLog);
}

function segmentSortOrder(key: string): [number, number, string] {
  if (key.startsWith('full-')) {
    return [0, Number(key.slice(5)) || 0, key];
  }
  if (key.startsWith('received-')) {
    return [1, 0, key];
  }
  return [2, 0, key];
}

export function transcriptLinesFromSegments(
  segments: Map<string, FormattedTranscriptLine>
): FormattedTranscriptLine[] {
  return Array.from(segments.entries())
    .sort(([keyA, a], [keyB, b]) => {
      if (a.timeSeconds !== b.timeSeconds) return a.timeSeconds - b.timeSeconds;
      const [groupA, indexA, tieA] = segmentSortOrder(keyA);
      const [groupB, indexB, tieB] = segmentSortOrder(keyB);
      if (groupA !== groupB) return groupA - groupB;
      if (indexA !== indexB) return indexA - indexB;
      return tieA.localeCompare(tieB);
    })
    .map(([, line]) => line);
}

export function seedTranscriptSegments(
  transcript: FormattedTranscriptLine[]
): Map<string, FormattedTranscriptLine> {
  const segments = new Map<string, FormattedTranscriptLine>();
  transcript.forEach((line, index) => {
    segments.set(`seed-${index}-${line.timeSeconds}`, line);
  });
  return segments;
}
